import { Component, useCallback, useEffect, useState } from 'react';
import { getToken, setToken } from './api.js';
import Pair from './pair.jsx';
import Player from './player.jsx';
import CornerBadge from './corner-badge.jsx';

// App is a tiny state machine:
//   * No token in localStorage → render <Pair /> until paired.
//   * Token present → render <Player />. If the player gets a 401 (token
//     revoked / screen deleted) it calls onUnauthorized which drops the
//     token and flips us back to <Pair />.
//
// Wrapped in an ErrorBoundary so a render error reloads the page after a
// short delay rather than leaving a white screen on a wall-mounted TV.

export default function App() {
  const [hasToken, setHasToken] = useState(() => !!getToken());

  const handlePaired = useCallback(() => setHasToken(true), []);
  const handleUnauthorized = useCallback(() => {
    setToken(null);
    setHasToken(false);
  }, []);

  // ── Site-wide 10× OK gesture ─────────────────────────────────────
  // Hidden Settings opener that works on EVERY screen in the React SPA
  // (pair screen, player screen, future routes). Press OK / Enter 10
  // times within 4 seconds; any other key resets the streak. Calls
  // OpsoraNative.openSettings() when running inside the Opsora Player
  // APK. In a regular browser the gesture is a no-op.
  useEffect(() => {
    const NEED = 10, WINDOW_MS = 4000;
    let taps = [];
    function onKey(e) {
      if (e.repeat) return;
      const isOk = e.key === 'Enter' || e.code === 'Enter' || e.code === 'NumpadEnter';
      if (!isOk) {
        taps = [];                       // reset streak on any non-OK key
        return;
      }
      const now = Date.now();
      taps = [...taps, now].filter(t => now - t <= WINDOW_MS);
      if (taps.length >= NEED) {
        taps = [];
        try {
          if (window.OpsoraNative?.openSettings) {
            window.OpsoraNative.openSettings();
          }
        } catch (_) { /* swallow */ }
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ── Cursor visibility ─────────────────────────────────────────────
  // index.html scopes `cursor: none` to `body.playing`. We add that class
  // only when the player is showing content, so the pair screen and any
  // future settings/error overlays keep a visible cursor for clicking.
  useEffect(() => {
    document.body.classList.toggle('playing', hasToken);
    return () => document.body.classList.remove('playing');
  }, [hasToken]);

  return (
    <ReloadBoundary>
      {hasToken
        ? <Player onUnauthorized={handleUnauthorized} />
        : <Pair onPaired={handlePaired} />}
      <CornerBadge onSignOut={handleUnauthorized} />
    </ReloadBoundary>
  );
}

class ReloadBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('[player] render crash, reloading in 5s:', error, info);
    setTimeout(() => window.location.reload(), 5000);
  }

  render() {
    if (this.state.error) {
      return (
        <div style={{
          width: '100%', height: '100%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#08061a', color: '#9ca3af',
          fontSize: 'clamp(14px, 1.4vw, 20px)',
          fontFamily: "'Space Mono', monospace",
        }}>
          Recovering…
        </div>
      );
    }
    return this.props.children;
  }
}
